import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faEdit, faSave, faUsers } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { Button, Col, Row } from 'reactstrap';

//API
import cep_api from '../../../../api/cep_api';
import tipoDeFiscalizacao_api from '../../../../api/tipoDeFIscalizacao_api';
import solicitacaoFiscalizacao_api from '../../../../api/solicitacaoFiscalizacao_api';

//Componentes
import TextField from '../../../../components/TextField';
import CustomMap from "../../../../components/CustomMap";
import TabelaListagem from '../../../../components/TabelaListagem/TabelaListagem';

//Context
import { useAuth } from '../../../../context/auth';

//Helpers
import { showMessage, showQuestion } from '../../../../helpers/message';
import mascaras from '../../../../helpers/mascaras';
import { objCampo, validarCampos } from '../../../../helpers/ValidadorDeCampos';

//Constants
import { actionsTable } from '../../../../constants/MaterialTable';
import { useNavigate } from 'react-router-dom';


function DadosFiscalicazao({ id, solicitacaoFiscalicao, setSolicitacaoFiscalicao, loadSolicitacaoFiscalizacao }) {
    //State para pegar lista dos tipos de fiscalizacao
    const [listTipoDeFiscalizacao, setListTipoDeFiscalizacao] = useState([]);

    const navigate = useNavigate();

    //Estado de controle das demandas proximas para agrupamento
    const [demandasProximas, setDemandasProximas] = useState([]);

    //State para pegar erros
    const [errors, setErrors] = useState({});

    const { setLoading } = useAuth();

    // Exibe tabela de agrupamento de demandas
    const [visibleAgrupamento, setVisibleAgrupamentos] = useState(false);

    //Cabeçalhos da lista de demandas para agrupamento
    const headers = [
        {
            value: "Data de Cadastro", objectValue: "data", align: "right", options: {
                customBodyRenderLite: (dataIndex) => {
                    return <>{moment(demandasProximas[dataIndex]?.dataHora).format("DD/MM/YYYY")}</>
                }
            }
        },
        { value: "Endereço", objectValue: "endereco", align: "right" },
        { value: "Origem", objectValue: "origem", align: "right" },
        { value: "Codigo", objectValue: "codigoItemFiscalizacao", align: "right" },
        { value: "Status", objectValue: "status", align: "right" },
        { value: "Agrupado", objectValue: "agrupado", align: "right" },
    ];

    const loadTipoDeFiscalizacao = () => {
        setLoading(true);
        tipoDeFiscalizacao_api.getListAtivos()
            .then(response => {
                response.data.map(value => {
                    value.value = value.id.toString();
                    value.label = value.identificacao;

                    if (solicitacaoFiscalicao && solicitacaoFiscalicao?.tipoFiscalizacaoServico_Id) {
                        if (value.tipoFiscalizacaoServicos.filter(v => v.id == solicitacaoFiscalicao?.tipoFiscalizacaoServico_Id).length > 0) {
                            setSolicitacaoFiscalicao(prevState => ({
                                ...prevState,
                                tipoFiscalizacao_id: value.id.toString()
                            }))
                        }
                    }
                    return value;
                })

                setListTipoDeFiscalizacao(response.data)
            })
            .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
            .finally(() => setLoading(false));
    };

    //Função que carrega as demandas mais proximas da demanda atual
    const loadDemandasProximas = (id) => {
        if (id != null) {
            setLoading(true);
            solicitacaoFiscalizacao_api.getListProximos(id).then(response => {
                setDemandasProximas(response.data);
            }).catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
                .finally(() => setLoading(false));
        }
    }

    useEffect(() => {
        loadTipoDeFiscalizacao();
        loadDemandasProximas(id);
    }, [id]);

    const visualizar = (rowData) => {
        navigate("/admin/demandas/ficha-demanda", { state: { id: rowData.id } })
    }

    const agrupar = (solicitacaoId) => {
        setLoading(true);
        solicitacaoFiscalizacao_api.agruparSolicitacoes({
            SolicitacaoFiscalizacaoPrincipal_Id: id,
            SolicitacaoFiscalizacaoAgrupada_Id: solicitacaoId
        }).then(response => {
            showMessage("Sucesso", "Agrupamento feito com sucesso", "success", () => loadDemandasProximas(id));
        })
            .catch(ex => showMessage("Erro", ex.Message, "error", null))
            .finally(f => setLoading(false));
    }

    const removerAgrupamento = (agrupamentoId) => {
        showQuestion("Tem certeza?", "Tem certeza de que deseja deletar este agrupamento?\n Esta ação é irreversível", "info",
            (confirmation) => {
                if (confirmation) {
                    setLoading(true);
                    solicitacaoFiscalizacao_api.deletarAgrupamento(agrupamentoId).then(response => {
                        showMessage("Sucesso", "Agrupamento removido com sucesso", "success", () => loadDemandasProximas(id));

                    })
                        .catch(ex => showMessage("Erro", ex.Message, "error", null))
                        .finally(f => setLoading(false));
                }
            })

    }

    const ControleAcaoAgrupamento = (rowData, actionStyle) => {
        //Controle dos endereços que podem ser editados na tabela
        if (rowData.solicitacaoFiscalizacaoAgrupamento_Id) {
            return actionsTable.remover(() => removerAgrupamento(rowData.solicitacaoFiscalizacaoAgrupamento_Id));
        } else {
            return actionsTable.dinamico(faUsers, () => agrupar(rowData.id));
        }
    }

    //Ações que a tabela terá
    const actions = [
        ControleAcaoAgrupamento,
        actionsTable.visualizar(visualizar),
    ];

    //Estado de controle dos campos obrigatorios 
    const [campos, setCampos] = useState([
        objCampo({ name: "cep", required: true }),
        objCampo({ name: "bairro", required: true }),
        objCampo({ name: "numero", required: true }),
        objCampo({ name: "cidade", required: true }),
        objCampo({ name: "logradouro", required: true }),
        objCampo({ name: "uf", required: true }),
        objCampo({ name: "tipoFiscalizacao_id", required: true }),
        objCampo({ name: "tipoFiscalizacaoServico_Id", required: true })
    ]);

    //Função de onChange dos campos select
    const onChangeSearchable = (value, e) => {
        const _solicitacaoFiscalicao = {
            ...solicitacaoFiscalicao,
            [e.name]: value ? value.value.toString() : null,
        }

        //Validando erros no campo especifico para checar em tempo de digitação se ele possui algum erro
        const _campos = campos.filter(campo => campo.name === e.name);
        const fieldError = validarCampos(_solicitacaoFiscalicao, _campos);

        const _errors = { ...errors };
        delete _errors[e.name];
        if (fieldError[1].length === 0)
            setErrors({ ..._errors });
        else
            setErrors({ ..._errors, ...fieldError[0] });

        setSolicitacaoFiscalicao(_solicitacaoFiscalicao);
    }

    //Função de onChange dos campos
    const onChange = (e) => {
        const { name, value } = e.target;
        const _solicitacaoFiscalicao = {
            ...solicitacaoFiscalicao,
            [name]: value
        }

        //Validando erros no campo especifico para checar em tempo de digitação se ele possui algum erro
        const _campos = campos.filter(campo => campo.name === name);
        const fieldError = validarCampos(_solicitacaoFiscalicao, _campos);

        const _errors = { ...errors };
        delete _errors[name];
        if (fieldError[1].length === 0)
            setErrors({ ..._errors });
        else
            setErrors({ ..._errors, ...fieldError[0] });

        setSolicitacaoFiscalicao({
            ...solicitacaoFiscalicao,
            [name]: value
        });
    }

    //onChange para o marker no mapa
    const onSelectLocation = (location) => {
        setLoading(true);
        cep_api.GeoCode({ latitude: location.lat, longitude: location.lng }).then(response => {
            response.data.uf = response.data.estado;
            response.data.numeroLogradouro = response.data.numero;
            delete response.data.latitude;
            delete response.data.longitude;
            setSolicitacaoFiscalicao({
                ...solicitacaoFiscalicao,
                ...{ latitude: location.lat, longitude: location.lng }
            });
        })
            .catch(ex => showMessage("Erro", ex.Message, "error", null))
            .finally(f => setLoading(false));
    }


    const onBlur = (e) => {
        if (e.target.value.length < 9) return;
        setLoading(false)
        cep_api.get(e.target.value)
            .then(response => {
                const _solicitacaoFiscalicao = {
                    ...solicitacaoFiscalicao,
                    logradouro: response.data.logradouro,
                    complemento: response.data.complemento,
                    bairro: response.data.bairro,
                    cidade: response.data.localidade,
                    uf: response.data.uf
                };

                const endereco = {
                    Cep: _solicitacaoFiscalicao.cep,
                    Bairro: _solicitacaoFiscalicao.bairro,
                    Logradouro: _solicitacaoFiscalicao.logradouro,
                    Cidade: _solicitacaoFiscalicao.cidade,
                    Estado: _solicitacaoFiscalicao.uf,
                    Numero: _solicitacaoFiscalicao.numero,
                    Latitude: "",
                    Longitude: "",
                    EnderecoFormatado: "",
                }
                cep_api.GeoCode(endereco).then(response => {
                    if (response.data.latitude && response.data.longitude) {
                        _solicitacaoFiscalicao.latitude = parseFloat(response.data.latitude.replace(",", "."));
                        _solicitacaoFiscalicao.longitude = parseFloat(response.data.longitude.replace(",", "."));
                    }
                    setSolicitacaoFiscalicao(_solicitacaoFiscalicao)
                }).catch(e => {
                    showMessage("Aviso", e.message, "error");
                }).finally(f => setLoading(false));

            })
            .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
            .finally(() => setLoading(false));
    }

    //Função para salvar e ir para proximos step
    const handleOnSumitDadosFiscalizacao = () => {
        const _errors = validarCampos(solicitacaoFiscalicao, campos);
        setErrors(_errors[0]);

        if (_errors[1].length > 0)
            return true;

        const _solicitacaoFiscalicao = { ...solicitacaoFiscalicao }
        if (_solicitacaoFiscalicao.id) {
            setLoading(true)
            solicitacaoFiscalizacao_api.put(_solicitacaoFiscalicao)
                .then(response => {
                    showMessage("Sucesso", "Operação realizada com sucesso", "success", () => {
                        loadSolicitacaoFiscalizacao();
                    })
                })
                .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
                .finally(() => setLoading(false))
        } else {
            setLoading(true)
            solicitacaoFiscalizacao_api.post(_solicitacaoFiscalicao)
                .then(response => {
                    showMessage("Sucesso", "Operação realizada com sucesso", "success", () => {
                        loadSolicitacaoFiscalizacao(response.data);
                    })
                })
                .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
                .finally(() => setLoading(false))
        }
    }

    return <>
        <Row>
            <div className='d-flex align-items-center justify-content-between'>
                <h5 className='mb-3'>Dados da Fiscalização</h5>
                {solicitacaoFiscalicao.id &&
                    <Button color='primary' className='mb-2 text-end' onClick={() => setVisibleAgrupamentos(!visibleAgrupamento)}>
                        Agrupar Demandas
                    </Button>
                }
            </div>
            {solicitacaoFiscalicao.situacao != "Agrupado" && visibleAgrupamento &&
                < TabelaListagem
                    actions={actions}
                    title={"Agrupar demandas"}
                    headers={headers}
                    items={demandasProximas}
                />
            }

            {
                !!solicitacaoFiscalicao.id &&
                <Col md={12} lg={3}>
                    <TextField
                        type={"text"}
                        name={"protocolo"}
                        label={"* Protocolo"}
                        value={solicitacaoFiscalicao.protocolo}
                        onChange={onChange}
                        maxLength={30}
                        disabled={!!solicitacaoFiscalicao.id}
                    />
                </Col>
            }

            <Col md={12} sm={12} lg={2}>
                <TextField
                    type={"text"}
                    label={"Origem"}
                    value={solicitacaoFiscalicao.origem}
                    disabled={true}
                />
            </Col>

            <Col md={12} lg={2}>
                <TextField
                    type={"select"}
                    label={"Permite Execução"}
                    name={"permitiExecucao"}
                    onChange={onChange}
                    options={[
                        { label: "Sim", value: "true" },
                        { label: "Não", value: "false" }
                    ]}
                    value={solicitacaoFiscalicao.permitiExecucao}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>

            <Col md={12} lg={!!solicitacaoFiscalicao.id ? 2 : 3}>
                <TextField
                    type={"text"}
                    mask={mascaras.cepMask}
                    name={"cep"}
                    label={"* CEP"}
                    value={solicitacaoFiscalicao.cep}
                    onBlur={onBlur}
                    onChange={onChange}
                    maxLength={9}
                    invalid={!!errors.cep}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
            <Col md={12} lg={!!solicitacaoFiscalicao.id ? 2 : 4}>
                <TextField
                    type={"text"}
                    name={"cidade"}
                    label={"* Cidade"}
                    value={solicitacaoFiscalicao.cidade}
                    onChange={onChange}
                    invalid={!!errors.cidade}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>

            <Col md={12} lg={1}>
                <TextField
                    type={"text"}
                    name={"uf"}
                    label={"* UF"}
                    value={solicitacaoFiscalicao.uf}
                    onChange={onChange}
                    invalid={!!errors.uf}
                    maxLength={2}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
            <Col md={12} lg={5}>
                <TextField
                    type={"text"}
                    name={"logradouro"}
                    label={"* Logradouro"}
                    value={solicitacaoFiscalicao.logradouro}
                    onChange={onChange}
                    maxLength={150}
                    invalid={!!errors.logradouro}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
            <Col md={12} lg={5}>
                <TextField
                    type={"text"}
                    name={"bairro"}
                    label={"* Bairro"}
                    value={solicitacaoFiscalicao.bairro}
                    onChange={onChange}
                    invalid={!!errors.bairro}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
            <Col md={12} lg={2}>
                <TextField
                    type={"text"}
                    name={"numero"}
                    label={"* Numero"}
                    value={solicitacaoFiscalicao.numero}
                    mask={mascaras.numeroMask}
                    invalid={!!errors.numero}
                    onChange={onChange}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>

            <Col md={12} lg={3}>
                <TextField
                    type={"text"}
                    name={"complemento"}
                    label={"Complemento"}
                    value={solicitacaoFiscalicao.complemento}
                    onChange={onChange}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
            <Col md={12} lg={5}>
                <TextField
                    type={"select"}
                    name={"tipoFiscalizacao_id"}
                    label={"* Tipo de fiscalização"}
                    searchable
                    options={listTipoDeFiscalizacao}
                    onChange={onChangeSearchable}
                    value={listTipoDeFiscalizacao.filter(value => value.value == solicitacaoFiscalicao.tipoFiscalizacao_id)}
                    invalid={!!errors.tipoFiscalizacao_id}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
            <Col md={12} lg={4}>
                <TextField
                    name={"tipoFiscalizacaoServico_Id"}
                    label={<>* Tipo de serviço</>}
                    searchable
                    type={"select"}
                    options={listTipoDeFiscalizacao
                        .filter(value => value.value == solicitacaoFiscalicao.tipoFiscalizacao_id)[0]?.tipoFiscalizacaoServicos
                        .map(value => {
                            value.label = value.identificacao;
                            value.value = value.id;
                            return value;
                        })}
                    onChange={onChangeSearchable}
                    value={listTipoDeFiscalizacao
                        .filter(value => value.value == solicitacaoFiscalicao.tipoFiscalizacao_id)[0]?.tipoFiscalizacaoServicos
                        .filter(v => v.id == solicitacaoFiscalicao.tipoFiscalizacaoServico_Id)
                        .map(value => {
                            value.label = value.identificacao;
                            value.value = value.id;
                            return value;
                        })}
                    invalid={!!errors.tipoFiscalizacaoServico_Id}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>




            {solicitacaoFiscalicao.codigoItemFiscalizado != null &&
                <Col md={12} lg={6}>
                    <TextField
                        type={"text"}
                        name={"codigoItemFiscalizado"}
                        label={"Código item fiscalizado"}
                        value={solicitacaoFiscalicao.codigoItemFiscalizado}
                        onChange={onChange}
                        disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                    />
                </Col>
            }

            {solicitacaoFiscalicao.entidadeFiscalizada != null &&
                <Col md={12} lg={6}>
                    <TextField
                        type={"text"}
                        name={"entidadeFiscalizada"}
                        label={"Entidade fiscalizada"}
                        value={solicitacaoFiscalicao.entidadeFiscalizada}
                        onChange={onChange}
                        disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                    />
                </Col>
            }

            <Col md={12} lg={12}>
                <TextField
                    type={"text"}
                    name={"observacoes"}
                    label={"Observações"}
                    onChange={onChange}
                    value={solicitacaoFiscalicao.observacoes}
                    invalid={!!errors.observacoes}
                    disabled={(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao == "Pendente") || solicitacaoFiscalicao.origem != "Interna")}
                />
            </Col>
        </Row>

        {solicitacaoFiscalicao && solicitacaoFiscalicao?.imagens?.length > 0 &&
            <Row className='mb-2'>
                <h5 className='mb-3'>Images</h5>
            </Row>
        }

        <div className='d-flex gap-3 justify-content-center flex-wrap px-2 py-2 mb-2'>
            {solicitacaoFiscalicao && solicitacaoFiscalicao?.imagens?.length > 0 && solicitacaoFiscalicao?.imagens?.map((img, key) => {
                return <div className='d-flex flex-column align-items-center justify-content-center' key={key}>
                    <a href={img.nomeArquivo} target="_blank" rel="noopener noreferrer">
                        <img
                            className='rounded'
                            src={img.nomeArquivo}
                            alt="Imagem da solicitação fiscalização"
                            width={90}
                        />
                    </a>
                    <span className='bg-secondary text-white p-1 mt-2 rounded-3' style={{ fontSize: "8pt" }}>Imagem da demanda</span>
                </div>
            })}
        </div>
        {
            solicitacaoFiscalicao.finalizacaoSemFiscalizacao &&
            <>

                <div>
                    <h5 className='mb-3'>Dados da Finalização</h5>
                </div>

                <Row className='text-secondary'>
                    <Col md={12}>
                        <p><span className="fw-bold">Data/Hora: </span> {moment(solicitacaoFiscalicao.finalizacaoSemFiscalizacao.dataHora).format("DD/MM/YYYY HH:mm:ss")}</p>
                    </Col>
                    <Col md={12}>
                        <p><span className="fw-bold">Descrição: </span> {solicitacaoFiscalicao.finalizacaoSemFiscalizacao.justificativa}</p>
                    </Col>
                    {
                        solicitacaoFiscalicao.finalizacaoSemFiscalizacao.arquivo &&
                        <Col md={12} className='d-flex flex-column'>
                            <span className="fw-bold mb-3">Imagem: </span>
                            <img
                                className='ms-3 mb-4 rounded-2'
                                width={100}
                                src={solicitacaoFiscalicao.finalizacaoSemFiscalizacao.arquivo}
                            />
                        </Col>
                    }
                </Row>
            </>
        }
        {solicitacaoFiscalicao && solicitacaoFiscalicao?.latitude != "" && solicitacaoFiscalicao?.longitude != "" &&
            <>
                <Row className='mb-2'>
                    <h5 className='mb-3'>Localização</h5>

                </Row>
                <Row style={{ height: "300px" }} className='mb-2 px-3'>
                    <Col md="12" lg="12" className="rounded overflow-hidden p-0" style={{ zIndex: "0" }} >
                        <CustomMap
                            addNewMarker
                            markers={[{ coordenadas: [solicitacaoFiscalicao.latitude ?? "", solicitacaoFiscalicao.longitude ?? ""] }]}
                            setPosition={onSelectLocation}
                            positionCenter
                        />
                    </Col>
                </Row>
            </>
        }

        <Row>
            <Col md={12} lg={12} className={`d-flex justify-content-center ${(!(solicitacaoFiscalicao.origem == "Interna" && solicitacaoFiscalicao.situacao != "Executado") || solicitacaoFiscalicao.origem != "Interna") ? "d-none" : ""}`}>
                <Button
                    color={solicitacaoFiscalicao.id ? 'warning text-white' : 'success'}
                    onClick={handleOnSumitDadosFiscalizacao}
                >
                    {solicitacaoFiscalicao.id ? <><FontAwesomeIcon icon={faEdit} /> Atualizar</> : <><FontAwesomeIcon icon={faSave} /> Salvar</>}
                </Button>
            </Col>
        </Row>
    </>;
}

export default DadosFiscalicazao;